/**
 * Every code-split page, defined exactly once.
 *
 * Both the route tables and the prefetcher import from here, which matters:
 * preload() and render must share the same in-flight promise, or a warmed
 * chunk would be fetched a second time on navigation and the warm-up would
 * have bought nothing.
 *
 * Pages are imported by their own module path, never through ../pages — a
 * barrel re-export pulls every page into whichever chunk touches it and
 * silently defeats splitting.
 */

import { eagerRoute, lazyRoute } from "./lazyRoute";
// Eager: menu is the near-certain next page after home (see prefetchOrder
// below) and code-splitting it was implicated in an intermittent scroll
// mis-position on the first navigation into it. Bundled directly instead of
// chasing the exact timing race.
import MenuPage from "../pages/MenuPage";

export const pages = {
  home: lazyRoute("home", () => import("../pages/HomePage")),
  menu: eagerRoute("menu", MenuPage),
  offers: lazyRoute("offers", () => import("../pages/OffersPage")),
  checkout: lazyRoute("checkout", () => import("../pages/CheckoutPage")),
  orderConfirmation: lazyRoute(
    "order-complete",
    () => import("../pages/OrderConfirmationPage"),
  ),
  giftcardPurchase: lazyRoute(
    "giftcard-purchase",
    () => import("../pages/giftcard/purchase"),
    (m) => m.GiftcardPurchasePage as never,
  ),

  profile: lazyRoute("profile", () => import("../pages/ProfilePage")),
  address: lazyRoute("address", () => import("../pages/AddressPage")),
  payments: lazyRoute("payments", () => import("../pages/PaymentPage")),
  orders: lazyRoute("orders", () => import("../pages/OrdersPage")),
  loyalty: lazyRoute("loyalty", () => import("../pages/LoyaltyPage")),
  giftCards: lazyRoute("gift-cards", () => import("../pages/GiftCardsPage")),
} as const;

export type PageKey = keyof typeof pages;

/**
 * Route patterns per page, used to match a link's href back to the chunk that
 * will render it. Order within a page does not matter; matchPath is exact.
 */
export const pagePatterns: Record<PageKey, string[]> = {
  home: ["/", "/home"],
  menu: ["/menu", "/menu/:categoryId", "/menu/:categoryId/:productId"],
  offers: ["/offer-and-rewards"],
  checkout: ["/checkout"],
  orderConfirmation: ["/orders/:orderId", "/order-complete/:orderId"],
  giftcardPurchase: ["/giftcard/purchase"],
  profile: ["/profile"],
  address: ["/address"],
  payments: ["/payments"],
  orders: ["/orders"],
  loyalty: ["/loyalty-points"],
  giftCards: ["/gift-cards"],
};

/**
 * Warm-up order, most-likely-next first.
 *
 * A customer lands on home, goes to the menu, then checkout. Account pages are
 * last because most sessions never open them — but they are still warmed, so
 * that no navigation in the app is ever a cold fetch.
 */
export const prefetchOrder: PageKey[] = [
  "checkout",
  "offers",
  "orderConfirmation",
  "home",
  "orders",
  "profile",
  "loyalty",
  "giftCards",
  "address",
  "payments",
  "giftcardPurchase",
];
